import React from 'react';
import { Link } from 'react-router-dom';

const BACKEND_ROOT = 'https://maineventbackend.seuytel.com';
const STORAGE = `${BACKEND_ROOT}/storage/`;

export const EVENT_CATEGORY_LOOK = [
    { id: 1, key: 'cat.music', icon: '♪', color: '#7C3AED' },
    { id: 2, key: 'cat.nightlife', icon: '☾', color: '#DB2777' },
    { id: 3, key: 'cat.festivals', icon: '⚑', color: '#F59E0B' },
    { id: 17, key: 'cat.culture', icon: '✦', color: '#8B5CF6' },
    { id: 6, key: 'cat.networking', icon: '▣', color: '#2563EB' },
    { id: 7, key: 'cat.workshops', icon: '✎', color: '#0891B2' },
    { id: 8, key: 'cat.startups', icon: '↗', color: '#16A34A' },
    { id: 19, key: 'cat.education', icon: '◈', color: '#4F46E5' },
    { id: 9, key: 'cat.food', icon: '◐', color: '#EA580C' },
    { id: 11, key: 'cat.fashion', icon: '◆', color: '#E11D48' },
    { id: 13, key: 'cat.sports', icon: '●', color: '#059669' },
    { id: 14, key: 'cat.wellness', icon: '☼', color: '#0D9488' },
] as const;

export type WebEvent = {
    id: number | string;
    slug?: string;
    title?: string;
    images?: string[];
    image?: string;
    location?: any;
    city?: string;
    country?: string;
    date?: Array<{ start_date?: string | null; end_date?: string | null }>;
    start_date?: string | null;
    end_date?: string | null;
    ticket_price?: number;
    min_price?: number;
    currency?: string;
    is_online?: boolean;
    is_recurring?: boolean;
    is_event_pool?: boolean;
    [key: string]: any;
};

export function extractEvents(payload: any): WebEvent[] {
    const raw = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data?.data)
            ? payload.data.data
            : Array.isArray(payload?.data?.events)
                ? payload.data.events
                : Array.isArray(payload?.data)
                    ? payload.data
                    : Array.isArray(payload?.events)
                        ? payload.events
                        : Array.isArray(payload?.results)
                            ? payload.results
                            : [];
    return raw.filter((item: any) => item && (item.id || item.slug));
}

export function eventHref(event: WebEvent): string {
    return `/event/${event.slug || event.id}`;
}

export function eventImage(event: WebEvent): string {
    const raw = event.images?.[0] || event.image || event.image_path || '';
    if (!raw) return '';
    return /^https?:/.test(raw) ? raw : `${STORAGE}${raw}`;
}

function parseDate(raw?: string | null): Date | null {
    if (!raw) return null;
    const date = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
    return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(event: WebEvent): string {
    const date = event.date?.[0] || { start_date: event.start_date, end_date: event.end_date };
    const start = parseDate(date?.start_date || date?.end_date);
    if (!start) return '';
    return start.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
}

function formatLocation(event: WebEvent): string {
    if (event.is_online) return 'Online';
    const loc = Array.isArray(event.location) ? event.location[0] : event.location;
    const city = loc?.city || event.city || '';
    const country = loc?.country || event.country || '';
    return [city, country].filter(Boolean).join(', ');
}

function formatPrice(event: WebEvent): string {
    const value = event.ticket_price ?? event.min_price;
    if (value === undefined || value === null) return '';
    if (Number(value) === 0) return 'Free';
    const currency = event.currency || 'EUR';
    try {
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(Number(value));
    } catch {
        return `${Number(value).toFixed(2)} ${currency}`;
    }
}

export function EventCard({ event }: { event: WebEvent }) {
    const img = eventImage(event);
    const date = formatDate(event);
    const location = formatLocation(event);
    const price = formatPrice(event);

    return (
        <Link to={eventHref(event)} className="se-event-card">
            <div className="se-event-card-media">
                {img ? <img src={img} alt={event.title || 'Event'} loading="lazy" /> : <span className="se-event-card-empty">Main Events</span>}
                {date && <span className="se-event-date-badge">{date}</span>}
                {(event.is_recurring || event.is_event_pool) && (
                    <span className="se-event-kind-badge">{event.is_recurring ? 'Repeat' : 'Pool'}</span>
                )}
            </div>
            <div className="se-event-card-body">
                <h3>{event.title || 'Event'}</h3>
                {location && <p className="se-event-card-location">{location}</p>}
                <div className="se-event-card-foot">
                    <span>{price || 'Details'}</span>
                    <span>Tickets</span>
                </div>
            </div>
        </Link>
    );
}

export function EventGrid({ events, emptyText }: { events: WebEvent[]; emptyText?: string }) {
    if (!events.length) {
        return <p className="se-event-empty">{emptyText || 'Keine Events gefunden.'}</p>;
    }

    return (
        <div className="se-event-grid">
            {events.map((event) => (
                <EventCard key={`${event.id}-${event.slug || ''}`} event={event} />
            ))}
        </div>
    );
}

export function EventGridSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div className="se-event-grid se-event-grid-loading" aria-hidden="true">
            {Array.from({ length: count }).map((_, index) => (
                <article className="se-event-card se-event-skeleton-card" key={index}>
                    <div className="se-event-card-media se-event-skeleton-thumb" />
                    <div className="se-event-card-body">
                        <span className="se-event-skeleton-line se-event-skeleton-title" />
                        <span className="se-event-skeleton-line se-event-skeleton-meta" />
                        <div className="se-event-card-foot">
                            <span className="se-event-skeleton-line se-event-skeleton-price" />
                            <span className="se-event-skeleton-line se-event-skeleton-action" />
                        </div>
                    </div>
                </article>
            ))}
        </div>
    );
}
